"use client";

import { Tooltip } from "./Tooltip";
import { YearPicker } from "./YearPicker";
import { Typography } from "./Typography";
import { Select } from "@/components/forms";
import { cn } from "@/lib/utils/cn";
import { AlertTriangle, Info, Sparkles } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type LabelValueDefaults = { valueAlign?: "left" | "right" };

export const LabelValueDefaultsContext = createContext<LabelValueDefaults>({});

const textAlignClass = {
  left: "text-left",
  right: "text-right",
} as const;

export interface LabelValueProps {
  label: string;
  value: string;
  highlight?: boolean;
  note?: string;
  missing?: boolean;
  editing?: boolean;
  inputType?: "text" | "number" | "date" | "year" | "checkbox" | "currency" | "textarea";
  placeholder?: string;
  decimals?: number;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  selectOptions?: { value: string; label: string }[];
  /** When "multiple" + selectOptions is provided, renders a multi-select. The
   * `inputValue` is a JSON-encoded string[] and `onInputChange` is called with
   * the JSON-encoded array. Kept as a string so it stays compatible with the
   * existing FormData-backed CompactCard flow. */
  selectMode?: "single" | "multiple";
  /** When true and selectOptions is provided, the empty "-" option is not shown. */
  required?: boolean;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  onClick?: () => void;
  aiInferred?: boolean;
  aiReason?: string;
  /** When provided and NOT in editing mode, renders this instead of the standard value display. */
  renderValue?: ReactNode;
  /** Opt-in: render the label's hover hint as a floating Tooltip component
   * instead of the native `title` attribute. Used on long labels (e.g. UW
   * questions) where the native tooltip is too plain; keep off elsewhere
   * to preserve current behaviour. */
  labelTooltip?: boolean;
  /** Horizontal alignment of the value/input text. Defaults to context
   * (LabelValueDefaultsContext) or "left" when none is provided. */
  valueAlign?: "left" | "right";
}

const FOCUS_RING_CLASS = "focus:ring-navigator-primary focus:border-navigator-primary";

export function LabelValue({
  label,
  value,
  highlight,
  note,
  missing,
  editing,
  inputType = "text",
  placeholder,
  decimals = 2,
  inputValue,
  onInputChange,
  selectOptions,
  selectMode = "single",
  required,
  prefix,
  suffix,
  min,
  max,
  onClick,
  aiInferred,
  aiReason,
  labelTooltip = false,
  renderValue,
  valueAlign,
}: LabelValueProps) {
  const valueRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const defaults = useContext(LabelValueDefaultsContext);
  const align = valueAlign ?? defaults.valueAlign ?? "left";

  const checkOverflow = useCallback(() => {
    const el = valueRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, []);

  // Shared label element. When `labelTooltip` is on, wrap in the floating
  // Tooltip component; otherwise keep the native `title` attribute so this
  // change is a no-op for every existing call site.
  const labelEl = labelTooltip ? (
    <Tooltip content={label} placement="top" className="inline-flex min-w-0 max-w-[60%]">
      <span className="text-sm text-gray-500 whitespace-nowrap truncate cursor-default block">
        {label}
      </span>
    </Tooltip>
  ) : (
    <span
      className="text-sm text-gray-500 whitespace-nowrap min-w-0 max-w-[60%] truncate"
      title={label}
    >
      {label}
    </span>
  );

  useEffect(() => {
    if (editing) return;
    const frame = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(frame);
  }, [value, checkOverflow, editing]);

  // Check if the current value is not a valid option
  // Always use inputValue (the raw form value) for validation, not the formatted display value
  const checkValue = inputValue ?? "";
  const isInvalidOption =
    selectMode !== "multiple" &&
    selectOptions && checkValue && !selectOptions.some((opt) => opt.value === checkValue);
  const isEditingEmpty = editing && missing && !checkValue;

  const formatWithCommas = (input: string): string => {
    if (input === "") return "";
    const sign = input.startsWith("-") ? "-" : "";
    const sanitized = input.replace(/[^0-9.]/g, "");
    const [intPartRaw, decPart] = sanitized.split(".");
    const intPart = intPartRaw ?? "";
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (decPart !== undefined && decPart.length > decimals) {
      const rounded = parseFloat(`${intPart}.${decPart}`).toFixed(decimals);
      const [rInt, rDec] = rounded.split(".");
      const rWithCommas = (rInt ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return rDec !== undefined ? `${sign}${rWithCommas}.${rDec}` : `${sign}${rWithCommas}`;
    }
    if (decPart !== undefined && decimals > 0) {
      return `${sign}${withCommas}.${decPart}`;
    }
    return `${sign}${withCommas}`;
  };

  const handleCurrencyChange = (raw: string) => {
    const sanitized = raw.replace(/[^0-9.-]/g, "");
    const parts = sanitized.split(".");
    if (decimals === 0 && parts.length > 1) return;
    if (parts.length > 2) return;
    const minusCount = (sanitized.match(/-/g) || []).length;
    if (minusCount > 1 || (sanitized.includes("-") && !sanitized.startsWith("-"))) return;
    if (parts.length > 1 && parts[1] && parts[1].length > decimals) {
      onInputChange?.(`${parts[0]}.${parts[1].substring(0, decimals)}`);
    } else {
      onInputChange?.(sanitized);
    }
  };

  // Editing mode: show inline input/select in same row layout
  if (editing && onInputChange) {
    const isChecked = inputValue === "true";
    const inputEl = inputType === "checkbox" ? (
      <button
        type="button"
        role="switch"
        aria-checked={isChecked}
        onClick={(e) => {
          e.stopPropagation();
          onInputChange(isChecked ? "false" : "true");
        }}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1",
          isChecked ? "bg-blue-600" : "bg-gray-200",
          FOCUS_RING_CLASS,
        )}
      >
        <span
          className={cn(
            "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
            isChecked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </button>
    ) : inputType === "year" ? (
      <div onClick={(e) => e.stopPropagation()}>
        <YearPicker
          value={inputValue ?? ""}
          onChange={onInputChange}
          textAlign={align}
        />
      </div>
    ) : inputType === "textarea" ? (
      <textarea
        placeholder={placeholder ?? label}
        value={inputValue ?? ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onInputChange(e.target.value)}
        rows={2}
        className={cn(
          "text-sm font-medium text-gray-900 bg-white border rounded-lg px-2 py-1 focus:outline-none focus:ring-1 min-w-0 w-full resize-y transition-colors",
          textAlignClass[align],
          isEditingEmpty ? "border-red-300 bg-red-50" : "border-gray-200",
          FOCUS_RING_CLASS,
        )}
      />
    ) : selectOptions && selectMode === "multiple" ? (
      <div onClick={(e) => e.stopPropagation()} className="min-w-0 w-full">
        <Select
          selectSize="xs"
          mode="multiple"
          placeholder="Select all that apply"
          searchable={selectOptions.length > 6}
          textAlign={align}
          options={selectOptions.filter((opt) => opt.value !== "")}
          value={(() => {
            const raw = inputValue ?? "";
            if (!raw) return [];
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
            } catch {
              return [];
            }
          })()}
          onChange={(value) => {
            const arr = Array.isArray(value) ? value : [];
            onInputChange(JSON.stringify(arr));
          }}
        />
      </div>
    ) : selectOptions ? (
      <div onClick={(e) => e.stopPropagation()}>
        <Select
          selectSize="xs"
          textAlign={align}
          placeholder="-"
          options={
            required
              ? selectOptions.filter((opt) => opt.value !== "")
              : selectOptions.some((opt) => opt.value === "")
                ? selectOptions
                : [{ value: "", label: "-" }, ...selectOptions]
          }
          value={inputValue ?? ""}
          onChange={(value) => onInputChange(value as string)}
          required={isEditingEmpty || !!isInvalidOption}
        />
      </div>
    ) : inputType === "currency" ? (
      <div className="relative flex items-center min-w-0 w-full">
        {prefix && (
          <span className="absolute left-2 text-sm text-gray-400 pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="text"
          inputMode="decimal"
          placeholder={placeholder ?? label}
          value={formatWithCommas(inputValue ?? "")}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleCurrencyChange(e.target.value)}
          className={cn(
            "h-8 text-sm font-medium text-gray-900 bg-white border rounded-lg focus:outline-none focus:ring-1 min-w-0 w-full tabular-nums transition-colors",
            textAlignClass[align],
            isEditingEmpty ? "border-red-300 bg-red-50" : "border-gray-200",
            FOCUS_RING_CLASS,
            prefix ? "pl-5 pr-2" : "px-2",
            suffix ? "pr-5" : "",
          )}
        />
        {suffix && (
          <span className="absolute right-2 text-sm text-gray-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    ) : (
      <div className="relative flex items-center min-w-0 w-full">
        {prefix && (
          <span className="absolute left-2 text-sm text-gray-400 pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type={inputType}
          placeholder={placeholder ?? label}
          value={inputValue ?? ""}
          min={min}
          max={max}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onInputChange(e.target.value)}
          className={cn(
            "h-8 text-sm font-medium text-gray-900 bg-white border rounded-lg focus:outline-none focus:ring-1 min-w-0 w-full tabular-nums transition-colors",
            textAlignClass[align],
            inputType === "number" &&
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0",
            inputType === "date" && align === "right" &&
              "[&::-webkit-datetime-edit]:block [&::-webkit-datetime-edit]:text-right [&::-webkit-datetime-edit-fields-wrapper]:w-full [&::-webkit-datetime-edit-fields-wrapper]:justify-end",
            isEditingEmpty ? "border-red-300 bg-red-50" : "border-gray-200",
            FOCUS_RING_CLASS,
            prefix ? "pl-5 pr-2" : "px-2",
            suffix ? "pr-5" : "",
          )}
        />
        {suffix && (
          <span className="absolute right-2 text-sm text-gray-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    );

    if (inputType === "textarea") {
      return (
        <div
          className={cn(
            "flex flex-col gap-1 px-2 py-1 rounded-sm overflow-hidden",
            onClick && "cursor-pointer hover:!bg-blue-50",
          )}
          onClick={onClick}
        >
          {labelEl}
          {inputEl}
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center gap-2 px-2 rounded-sm overflow-hidden h-9",
          onClick && "cursor-pointer hover:!bg-blue-50",
        )}
        onClick={onClick}
      >
        {labelEl}
        {aiInferred && (
          <Tooltip content={aiReason || "AI-Suggested"}>
            <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
          </Tooltip>
        )}
        <span className="shrink-0 min-w-6 flex-1" />
        {(isInvalidOption || isEditingEmpty) && (
          <Tooltip
            content={isInvalidOption ? `"${inputValue}" is not a valid option for ${label}` : `${label} is required`}
            placement="top"
          >
            <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
          </Tooltip>
        )}
        <span className={cn("shrink-0", inputType === "checkbox" ? "" : "min-w-0 w-[40%]")}>{inputEl}</span>
        {note && (
          <Tooltip content={note} placement="top">
            <Info className="w-3 h-3 text-amber-500 shrink-0" />
          </Tooltip>
        )}
      </div>
    );
  }

  // Read-only mode
  const showMissing = missing || isInvalidOption;

  const valueSpan = (
    <span
      ref={valueRef}
      onMouseEnter={checkOverflow}
      className={cn(
        "text-sm font-medium tabular-nums truncate block",
        textAlignClass[align],
        highlight ? "text-green-700 font-bold" : "text-gray-900",
      )}
    >
      {value}
    </span>
  );

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-2 py-1 rounded-sm overflow-hidden h-9",
        onClick && "cursor-pointer hover:!bg-blue-50",
      )}
      onClick={onClick}
    >
      {labelEl}
      {aiInferred && (
        <Tooltip content={aiReason || "AI-Suggested"}>
          <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
        </Tooltip>
      )}
      <span className="shrink-0 min-w-6 flex-1" />
      {showMissing && (
        <Tooltip
          content={isInvalidOption ? `"${value}" is not a valid option for ${label}` : `${label} is required`}
          placement="top"
        >
          <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
        </Tooltip>
      )}
      {renderValue ? (
        <span className="min-w-0 overflow-hidden">{renderValue}</span>
      ) : inputType === "checkbox" ? (
        <span
          className={cn(
            "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent opacity-60",
            value === "Yes" ? "bg-blue-400" : "bg-gray-300",
          )}
        >
          <span
            className={cn(
              "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
              value === "Yes" ? "translate-x-4" : "translate-x-0",
            )}
          />
        </span>
      ) : isOverflowing ? (
        <Tooltip
          content={value}
          placement="top"
          className="block min-w-0 overflow-hidden"
        >
          {valueSpan}
        </Tooltip>
      ) : (
        <span className="min-w-0 overflow-hidden">{valueSpan}</span>
      )}
      {note && (
        <Tooltip content={note} placement="top">
          <Info className="w-3 h-3 text-amber-500 shrink-0" />
        </Tooltip>
      )}
    </div>
  );
}

export function SectionTitle({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  /** Opt-in: show this string in a floating Tooltip on hover - useful when
   * the rendered title is truncated and the full label still matters. */
  tooltip?: string;
}) {
  const title = (
    <Typography variant="overline" as="h5">
      {children}
    </Typography>
  );
  if (!tooltip) return title;
  return (
    <Tooltip content={tooltip} placement="top" className="inline-flex max-w-full">
      <span className="cursor-default">{title}</span>
    </Tooltip>
  );
}
